export type FuneralType =
  | 'BURIAL'
  | 'CREMATION'
  | 'TREE_FUNERAL'
  | 'SEA_FUNERAL'
  | 'HOME_URN'
  | 'GEMSTONE_FUNERAL'

export enum FuneralTypeEnum {
  BURIAL = 'BURIAL',
  CREMATION = 'CREMATION',
  TREE_FUNERAL = 'TREE_FUNERAL',
  SEA_FUNERAL = 'SEA_FUNERAL',
  HOME_URN = 'HOME_URN',
  GEMSTONE_FUNERAL = 'GEMSTONE_FUNERAL'
}

export type PlanningReason =
  | 'ACUTE_BEREAVEMENT'
  | 'IMPENDING_BEREAVEMENT'
  | 'PROVISION'

export enum PlanningReasonEnum {
  ACUTE_BEREAVEMENT = 'ACUTE_BEREAVEMENT',
  IMPENDING_BEREAVEMENT = 'IMPENDING_BEREAVEMENT',
  PROVISION = 'PROVISION'
}

export type LocationType =
  | 'HOSPITAL'
  | 'ELDERLY_CARE'
  | 'HOME'
  | 'HOSPICE'
  | 'ANOTHER_UNDERTAKER'
  | 'OTHER'

export type BeneficiaryType =
  | 'SELF'
  | 'PARTNER'
  | 'PARENT'
  | 'GRANDPARENT'
  | 'OTHER'

export type ProvisionReason = 'INFO' | 'PLAN' | 'COMPARE'

export type TimeFrameType = 'NOW' | 'WEEKS' | 'MONTHS' | 'UNSURE'

export type ProgressType = 'PREPARED' | 'INSURANCE_SET' | 'STARTING'

export type GreetingType = 'MR' | 'MRS'

export type ReligionType =
  | 'CHRISTIAN_ROMAN_CATHOLIC'
  | 'CHRISTIAN_EVANGELICAL'
  | 'CHRISTIAN_ORTHODOX'
  | 'MUSLIM'
  | 'BUDDHIST'
  | 'JEWISH'
  | 'HINDU'
  | 'SIKH'
  | 'WITHOUT_CONFESSION'
  | 'OTHERS'

export type ProductType =
  | 'FUNERAL'
  | 'CEREMONY'
  | 'CEREMONY_OTHER'
  | 'COFFIN'
  | 'COFFIN_LINING'
  | 'URN'
  | 'FLOWER'
  | 'OBITUARY'
  | 'MEMORIAL'
  | 'CARD'
  | 'OTHERS'
  | 'OTHERS_FROM_ZOHO'
  | 'OTHERS_FROM_SALESFORCE'
  | 'TOMBS'

export type AppointmentType = 'PHONE' | 'PERSONAL'

export type TimeOfDay = 'MORNING' | 'MIDDAY' | 'AFTERNOON' | 'ALL_DAY'

export type FamilyStatus = 'SINGLE' | 'MARRIED' | 'WIDOWED' | 'DIVORCED'

interface Image {
  id: string
  url: string
}

interface Price {
  currency: string
  amount: number
  description: string
}

export interface Graveyard {
  uuid: string
  name: string
  address: string
  region: string
  country: string
  zipCode: string
  latitude: string
  longitude: string
  alreadyOwnGrave: boolean
}

export interface FuneralHome {
  name: string
  description: string
  image: string
  address: {
    street: string
    number: string
    zipCode: string
    city: string
  }
  contactDetails: {
    firstName: string
    lastName: string
    email: string
    phone: string
    website: string
  }
}

export interface Order {
  uuid: string
  orderId: string
  contactDetails: ContactDetailInput
  products: any[]
  location: Location
  planningReason: PlanningReason
  creationDate: string
  customerAgent: CustomerAgent
}

export interface OfferInput {
  uuid: string
  orderId: string
  creationDate: string
  location: Location
  funeralType: FuneralType
  planningReason: PlanningReason
  deceasedLocation?: AddressInput
  contactDetails: ContactDetailInput
  products: any[]
  externalFees: ExternalFee[]
  totalAmountFees: Price
  totalAmount: Price
  grandTotal: Price
  discounts: Product[]
  interests: any[]
  comments: string
  graveyard: Graveyard
  provision: ProvisionInput
  beneficiary: BeneficiaryType
  offerDownloadUrl: string
  documents: Document[]
  orderConfirmed: boolean
  interestedInFinancing: boolean
  appointment: Appointment
  syncWithZoho: boolean
  sendNotifications: boolean
  testOffer: boolean
  activeEditCard: boolean
  customerAgent: CustomerAgent
}

export interface Product {
  uuid: string
  name: string
  orderPriority: number
  shortDescription: string
  longDescription: string
  prices: Price[]
  photos: Image[]
  quantity?: number
  status: string
  productType: ProductType
  availableFor: FuneralType[]
  externalUrl: string
  total: Price
  category: string
}

export interface ExternalFee {
  uuid: string
  name: string
  description: string
  price: Price
  orderPriority: number
}

export interface Interest {
  uuid: string
  name: string
  shortDescription: string
  longDescription: string
  photos: Image[]
}

export interface Package {
  id: string
  name: string
  description: string
  image: string
}

export interface Document {
  type: string
  data: { key: string; value: any }[]
}

export interface ContactFormParams {
  name: string
  email: string
  subject: string
  message: string
  phone: string
}

export interface Appointment {
  type: AppointmentType
  time: TimeOfDay
  date: string
}

export interface MandateForm {
  deceased_greeting: GreetingType
  deceased_firstname: string
  deceased_lastname: string
  deceased_dateOfBirth: string
  deceased_placeOfBirth: string
  deceased_dateOfDeath: string
  deceased_familiyStatus: FamilyStatus
  deceased_lastAddress: string
  deceased_religionType: ReligionType
  customer_greeting: string
  customer_firstname: string
  customer_lastname: string
  customer_dateOfBirth: string
  customer_relation: string
  customer_address: string
  signature: string
  place: string
  clever: boolean
}

export class Location {
  public province: string
  public city: string
  public available: boolean
  public zipCode: string
  public latitude: string
  public longitude: string
  public label: string
  public displayCategories: boolean
}

export class CustomerAgent {
  public name: string
  public photo: string
}

export class AddressInput {
  public locationType: LocationType

  public street: string

  public number: string

  public zipCode: number

  public city: string

  public country: string
}

export class ProvisionInput {
  public provisionReason: ProvisionReason

  public progress: ProgressType

  public timeFrame: TimeFrameType

  public birthYear: string

  public includeTrustAccountCosts: boolean
}

export class ContactDetailInput {
  public greeting: GreetingType = 'MRS'

  public firstName: string = ''

  public lastName: string = ''

  public email: string = ''

  public phone: string = ''
}

export interface WizardInput {
  location: Location
  funeralType: FuneralType
  planningReason: PlanningReason
  deceasedLocation: AddressInput
  provision: ProvisionInput
  beneficiary: BeneficiaryType
  contactDetails: ContactDetailInput
  syncWithZoho: boolean
  sendNotifications: boolean
  testingModeEnabled: boolean
  gclId: string
  gaClientId: string
  previousPage: string
}



// WEBPACK FOOTER //
// ./src/resources/index.ts